/**
 * 產品的**代管設定**（`products.options.hosting_profile`，批141）——表單 ⇄ JSON 與前端預檢。
 *
 * ## 這份資料在管什麼
 *
 * 「這個產品的站台在我方主機上要怎麼開」：出貨 channel、DNS zone、三個目錄樣板、容器
 * 記憶體上限、資源門檻、六個功能旗標、首位管理員的登入方式與 setup 網址。
 * **依產品的差異全部住在這份資料裡**——LS 的程式碼（後端與這個 SPA）一個產品字面都不寫。
 *
 * ## 權威在後端
 *
 * 後端 `services/hosting_profile.py::validate` 是唯一的驗證者（寫入端點 422 帶
 * `{code, field, message}`）。本檔的 `validateProfile` 是它的**逐條鏡像**：同樣的檢查順序、
 * 同樣的 `field` 路徑、同樣的錯誤碼——目的只是讓操作者在按下儲存之前就看到紅字，
 * 不是第二道防線。⚠️ **改後端那一份要同步改這裡**（`hostingProfile.spec.ts` 有對拍）。
 *
 * 已知且刻意不追的差異：JSON 的 `1.0` 在 Python 是浮點（後端判 `invalid_type`），
 * 到了 JavaScript 已經是整數 1，前端看不出來——這種情況由後端的 422 接手。
 *
 * ## 表單 ⇄ JSON
 *
 * - `formFromProfile(raw)`：**寬容**——缺鍵、型別不對一律退回空白，不丟錯（壞掉的 profile
 *   也要能在表單上看得到、改得回來）。
 * - `profileFromForm(form)`：只做機械正規化（去頭尾空白、空字串的可為 null 欄位 → null、
 *   數字字串 → 整數），**不代為修正**任何值——跟後端「存進 DB 的就是操作者送進來的樣子」
 *   同一個立場。鍵序固定為後端 `TOP_KEYS` 的順序。
 */

// ---------------------------------------------------------------------------
// 常數（與後端 `services/hosting_profile.py` 逐項相同）
// ---------------------------------------------------------------------------

/** `products.options` 裡的鍵名。 */
export const PROFILE_KEY = 'hosting_profile'
export const PROFILE_VERSION = 1

export const TOP_KEYS = [
  'version',
  'channel',
  'dns',
  'paths',
  'mem_limits',
  'resources',
  'features',
  'login_methods',
  'setup_path',
] as const
/**
 * 選填的頂層鍵（批151 C6）：**缺席時存檔也不送**——既有產品的 profile 逐位元不變。
 * 出現時接在 `TOP_KEYS` 之後（與後端 `OPTIONAL_TOP_KEYS` 同序）。
 */
export const OPTIONAL_TOP_KEYS = ['site_secrets'] as const
/** `site_secrets` 每一項：`key`／`env`／`label` 必填、`hint` 選填（缺席＝null）。 */
export const SITE_SECRET_REQUIRED = ['key', 'env', 'label'] as const
export const SITE_SECRET_OPTIONAL = ['hint'] as const
export const SITE_SECRETS_MAX = 8
export const SITE_SECRET_LABEL_MAX = 60
export const SITE_SECRET_HINT_MAX = 200
/** 站台機密的環境變數名不可以這個前綴開頭（授權中心自己的設定命名空間）。 */
export const SITE_SECRET_ENV_DENY_PREFIX = 'IPO_LS_'
export const DNS_KEYS = ['zone', 'clouddns_managed_zone'] as const
export const PATH_KEYS = ['data_dir', 'install_dir', 'backup_dir'] as const
export const RESOURCE_KEYS = ['mem_mb', 'disk_gb_first', 'disk_gb_additional'] as const
export const FEATURE_KEYS = [
  'pub_url',
  'vendor_sso',
  'tunnel',
  'pgweb',
  'site_config',
  'sso_google_managed',
] as const
export const LOGIN_METHODS = ['password', 'google'] as const

export type PathKey = (typeof PATH_KEYS)[number]
export type ResourceKey = (typeof RESOURCE_KEYS)[number]
export type FeatureKey = (typeof FEATURE_KEYS)[number]
export type LoginMethod = (typeof LOGIN_METHODS)[number]

export const SETUP_TOKEN_PLACEHOLDER = '{setup_token}'
export const SLUG_PLACEHOLDER = '{slug}'
export const MEM_LIMITS_MAX_KEYS = 12
export const SETUP_PATH_MAX_LEN = 200
export const RESOURCE_RANGES: Record<ResourceKey, [number, number]> = {
  mem_mb: [128, 65536],
  disk_gb_first: [1, 2000],
  disk_gb_additional: [1, 2000],
}

const CHANNEL_RE = /^[a-z0-9][a-z0-9-]{0,31}$/
const PATH_RE = /^\/opt\/(data|backup)(\/[a-z0-9_-]+)*\/[a-z0-9_-]*\{slug\}[a-z0-9_-]*$/
const MEM_KEY_RE = /^[a-z][a-z0-9_]{0,23}$/
const MEM_VALUE_RE = /^[1-9][0-9]{0,5}[kmg]$/
const DOMAIN_RE =
  /^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/
const MANAGED_ZONE_RE = /^[a-z](?:[a-z0-9-]{0,61}[a-z0-9])?$/
const SETUP_PATH_RE = /^\/[A-Za-z0-9._~!$&'()*+,;=:@/?%{}-]*$/
const SITE_SECRET_KEY_RE = /^[a-z][a-z0-9_]{0,31}$/
const SITE_SECRET_ENV_RE = /^[A-Z][A-Z0-9_]{1,63}$/

// ---------------------------------------------------------------------------
// 顯示用標籤（中性文案：不寫任何產品名）
// ---------------------------------------------------------------------------

export const FEATURE_LABELS: Record<FeatureKey, { label: string; hint: string }> = {
  pub_url: { label: '公開網址', hint: '為站台另配一個對外公開的網址（pub.<代號>.<zone>）' },
  vendor_sso: { label: '供應商登入', hint: '我方員工可從中台以供應商身分登入站台支援' },
  tunnel: { label: '反向隧道', hint: '站台可經反向隧道接收外部通路的 webhook' },
  pgweb: { label: '資料庫檢視（pgweb）', hint: '開站時可選擇附帶唯讀的資料庫檢視容器' },
  site_config: { label: '主機設定下發', hint: '站台吃主機的設定樣板（環境變數），可派工改站台設定' },
  sso_google_managed: {
    label: '代管 Google 登入',
    hint: '開站時預設開啟由我方代管的 Google 登入（登入方式要允許 Google 時必須勾選）',
  },
}

export const LOGIN_METHOD_LABELS: Record<LoginMethod, string> = {
  password: '帳號密碼',
  google: 'Google 帳號',
}

/**
 * 出貨 channel（裁示 48，LS 1.0.62）：**單一值、本台專用**——每台授權中心各存自己開站要下發
 * 的那一個（ls-dev 填測試用、ls-prod 填正式用）。1.0.61 以前是 `channels` 兩格。
 */
export const CHANNEL_LABEL = '出貨 channel'
export const CHANNEL_HINT =
  '這台授權中心開站時下發給站台的 dl channel（ls-dev 填測試用、ls-prod 填正式用的 channel）；' +
  '小寫英數與連字號、最長 32 字。'
export const PATH_LABELS: Record<PathKey, string> = {
  data_dir: '資料目錄',
  install_dir: '安裝目錄',
  backup_dir: '備份目錄',
}
export const RESOURCE_LABELS: Record<ResourceKey, string> = {
  mem_mb: '每站記憶體需求（MB）',
  disk_gb_first: '第一站磁碟需求（GB）',
  disk_gb_additional: '增站磁碟需求（GB）',
}

// ---------------------------------------------------------------------------
// 型別
// ---------------------------------------------------------------------------

/** 驗證過的 profile 形狀（`version: 1`）。 */
export interface HostingProfile {
  version: number
  channel: string
  dns: { zone: string | null; clouddns_managed_zone: string | null }
  paths: Record<PathKey, string>
  mem_limits: Record<string, string>
  resources: Record<ResourceKey, number | null>
  features: Record<FeatureKey, boolean>
  login_methods: string[]
  setup_path: string
  /** 批151：選填。這個產品的代管站需要我方替它設定哪些機密（值不在這裡）。 */
  site_secrets?: SiteSecretDecl[]
}

/** 站台機密欄位的宣告（`key`＝授權中心內部的欄位鍵、`env`＝寫進站台環境的變數名）。 */
export interface SiteSecretDecl {
  key: string
  env: string
  label: string
  hint: string | null
}

/** 站台機密宣告的一列（表單可增刪；字串欄一律存字串）。 */
export interface SecretRow {
  key: string
  env: string
  label: string
  hint: string
}

/** 後端與前端預檢共用的錯誤形狀（後端 422 的 `detail`）。 */
export interface ProfileIssue {
  code: 'invalid_type' | 'unknown_key' | 'missing_key' | 'invalid_value' | string
  /** 點分路徑（陣列用 `[i]`；根＝空字串）。 */
  field: string
  message: string
}

/** 記憶體上限的一列（表單可增刪）。 */
export interface MemRow {
  key: string
  value: string
}

/**
 * 結構化表單的狀態。**數字欄一律存字串**（空字串＝null＝沿用系統）——`v-model` 綁
 * `type=number` 會把值轉成數字、清空時變 `''`，兩種型別混在一起只會讓正規化更難寫。
 */
export interface ProfileForm {
  channel: string
  dnsZone: string
  dnsManagedZone: string
  paths: Record<PathKey, string>
  mem: MemRow[]
  resources: Record<ResourceKey, string>
  features: Record<FeatureKey, boolean>
  login: Record<LoginMethod, boolean>
  setupPath: string
  /** 批151：站台機密欄位的宣告（整列空白略過）。 */
  secrets: SecretRow[]
  /**
   * 讀進來的 profile 本來就有 `site_secrets` 這個鍵（含空陣列）。沒有列又本來沒有＝存檔
   * **不送**這個鍵；本來有、把列刪光＝送空陣列（語意相同，但讓「還原」後不顯示有變更）。
   */
  secretsPresent: boolean
}

// ---------------------------------------------------------------------------
// 表單 ⇄ JSON
// ---------------------------------------------------------------------------

/** 全新的空表單（新建代管設定用）：功能全關、登入只給帳號密碼、一列空白的記憶體上限。 */
export function emptyForm(): ProfileForm {
  return {
    channel: '',
    dnsZone: '',
    dnsManagedZone: '',
    paths: { data_dir: '', install_dir: '', backup_dir: '' },
    mem: [{ key: '', value: '' }],
    resources: { mem_mb: '', disk_gb_first: '', disk_gb_additional: '' },
    features: {
      pub_url: false,
      vendor_sso: false,
      tunnel: false,
      pgweb: false,
      site_config: false,
      sso_google_managed: false,
    },
    login: { password: true, google: false },
    setupPath: '',
    secrets: [],
    secretsPresent: false,
  }
}

function isObj(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === 'object' && !Array.isArray(v)
}
function str(v: unknown): string {
  return typeof v === 'string' ? v : ''
}
function numStr(v: unknown): string {
  if (typeof v === 'number' && Number.isFinite(v)) return String(v)
  if (typeof v === 'string') return v
  return ''
}

/** 任何值（含 null／壞資料）→ 表單。寬容：看不懂的地方一律空白。 */
export function formFromProfile(raw: unknown): ProfileForm {
  const f = emptyForm()
  if (!isObj(raw)) return f
  // 舊形狀（1.0.61 以前的 `channels` 兩格）不猜是哪一格：留空，讓操作者填這台的值。
  f.channel = str(raw.channel)
  const dns = isObj(raw.dns) ? raw.dns : {}
  f.dnsZone = str(dns.zone)
  f.dnsManagedZone = str(dns.clouddns_managed_zone)
  const paths = isObj(raw.paths) ? raw.paths : {}
  f.paths = { data_dir: str(paths.data_dir), install_dir: str(paths.install_dir), backup_dir: str(paths.backup_dir) }
  const mem = isObj(raw.mem_limits) ? raw.mem_limits : {}
  const rows = Object.entries(mem).map(([key, value]) => ({ key, value: numStr(value) }))
  f.mem = rows.length ? rows : [{ key: '', value: '' }]
  const res = isObj(raw.resources) ? raw.resources : {}
  f.resources = {
    mem_mb: numStr(res.mem_mb),
    disk_gb_first: numStr(res.disk_gb_first),
    disk_gb_additional: numStr(res.disk_gb_additional),
  }
  const feat = isObj(raw.features) ? raw.features : {}
  for (const k of FEATURE_KEYS) f.features[k] = feat[k] === true
  const methods = Array.isArray(raw.login_methods) ? raw.login_methods : []
  f.login = { password: methods.includes('password'), google: methods.includes('google') }
  f.setupPath = str(raw.setup_path)
  f.secretsPresent = 'site_secrets' in raw
  const secrets = Array.isArray(raw.site_secrets) ? raw.site_secrets : []
  f.secrets = secrets.map((item) => {
    const o = isObj(item) ? item : {}
    return { key: str(o.key), env: str(o.env), label: str(o.label), hint: str(o.hint) }
  })
  return f
}

/** 整列空白的機密宣告列＝多按了一次「新增」，不算資料。 */
function blankSecret(r: SecretRow): boolean {
  return !r.key.trim() && !r.env.trim() && !r.label.trim() && !r.hint.trim()
}

/**
 * 機密宣告表單第 `index` 列 → 送出陣列（`site_secrets[i]`）的 i（空白列不算；空白列本身回 -1）。
 * 錯誤的 `field` 用的是送出陣列的索引，把紅字標回表單那一列要先換算。
 */
export function secretSentIndex(rows: SecretRow[], index: number): number {
  if (!rows[index] || blankSecret(rows[index])) return -1
  return rows.slice(0, index).filter((r) => !blankSecret(r)).length
}

/**
 * 資源欄的字串 → JSON 值：空白＝null；整數字面＝整數；其餘**原樣送字串**
 * （讓預檢／後端回 `invalid_type`，而不是在這裡偷偷變成 null）。
 */
function resourceValue(s: string): number | string | null {
  const t = s.trim()
  if (!t) return null
  if (/^-?\d+$/.test(t)) return Number(t)
  return t
}

/** 表單 → 送給後端的 JSON（鍵序＝`TOP_KEYS`）。 */
export function profileFromForm(f: ProfileForm): Record<string, unknown> {
  const mem: Record<string, string> = {}
  for (const row of f.mem) {
    const key = row.key.trim()
    const value = row.value.trim()
    if (!key && !value) continue // 整列空白＝使用者多按了一次「新增」，不算資料
    mem[key] = value
  }
  const zone = f.dnsZone.trim()
  const managed = f.dnsManagedZone.trim()
  // 批151：站台機密——去頭尾空白、說明空白＝null、整列空白略過；沒有列又本來沒有這個鍵就不送。
  const secrets = (f.secrets ?? [])
    .filter((r) => !blankSecret(r))
    .map((r) => ({ key: r.key.trim(), env: r.env.trim(), label: r.label.trim(), hint: r.hint.trim() || null }))
  const optional: Record<string, unknown> =
    secrets.length || f.secretsPresent ? { site_secrets: secrets } : {}
  return {
    version: PROFILE_VERSION,
    channel: f.channel.trim(),
    dns: { zone: zone || null, clouddns_managed_zone: managed || null },
    paths: {
      data_dir: f.paths.data_dir.trim(),
      install_dir: f.paths.install_dir.trim(),
      backup_dir: f.paths.backup_dir.trim(),
    },
    mem_limits: mem,
    resources: {
      mem_mb: resourceValue(f.resources.mem_mb),
      disk_gb_first: resourceValue(f.resources.disk_gb_first),
      disk_gb_additional: resourceValue(f.resources.disk_gb_additional),
    },
    features: Object.fromEntries(FEATURE_KEYS.map((k) => [k, f.features[k] === true])),
    login_methods: LOGIN_METHODS.filter((m) => f.login[m]),
    setup_path: f.setupPath.trim(),
    ...optional,
  }
}

/** 給「JSON 檢視」用的排版（兩格縮排、鍵序照 `TOP_KEYS`＋`OPTIONAL_TOP_KEYS`）。 */
export function profileJson(value: unknown): string {
  if (!isObj(value)) return JSON.stringify(value, null, 2)
  const ordered: Record<string, unknown> = {}
  for (const k of [...TOP_KEYS, ...OPTIONAL_TOP_KEYS]) if (k in value) ordered[k] = value[k]
  for (const [k, v] of Object.entries(value)) if (!(k in ordered)) ordered[k] = v
  return JSON.stringify(ordered, null, 2)
}

/**
 * 兩份 profile 內容是否相同（**物件鍵序不算**、陣列順序算）。
 *
 * ★ 不能直接比 `JSON.stringify`：profile 存在 JSONB 裡，讀回來的物件鍵序是 PG 的內部排序
 *   （`paths` 可能變成 data_dir、backup_dir、install_dir），與表單轉出來的順序不同——
 *   直接比字串會讓一份沒動過的設定永遠顯示「有尚未儲存的變更」。
 */
export function sameProfile(a: unknown, b: unknown): boolean {
  return canonical(a) === canonical(b)
}
function canonical(v: unknown): string {
  if (Array.isArray(v)) return `[${v.map(canonical).join(',')}]`
  if (isObj(v)) {
    const keys = Object.keys(v).sort()
    return `{${keys.map((k) => `${JSON.stringify(k)}:${canonical(v[k])}`).join(',')}}`
  }
  return JSON.stringify(v ?? null)
}

// ---------------------------------------------------------------------------
// 前端預檢（後端 `validate()` 的逐條鏡像）
// ---------------------------------------------------------------------------

class Issue extends Error {
  constructor(
    public code: string,
    public field: string,
    message: string,
  ) {
    super(message)
  }
}

/** Python 的型別名（錯誤訊息與後端逐字相同用）。 */
function pyType(v: unknown): string {
  if (v === null || v === undefined) return 'null'
  if (typeof v === 'boolean') return 'bool'
  if (typeof v === 'number') return Number.isInteger(v) ? 'int' : 'float'
  if (typeof v === 'string') return 'str'
  if (Array.isArray(v)) return 'list'
  return 'dict'
}

function requireObject(v: unknown, field: string): Record<string, unknown> {
  if (!isObj(v)) throw new Issue('invalid_type', field, `必須是物件，收到 ${pyType(v)}`)
  return v
}

function exactKeys(
  obj: Record<string, unknown>,
  keys: readonly string[],
  field: string,
  optional: readonly string[] = [],
) {
  const prefix = field ? `${field}.` : ''
  for (const k of Object.keys(obj)) {
    if (!keys.includes(k) && !optional.includes(k)) {
      throw new Issue('unknown_key', `${prefix}${k}`, `不認得的鍵「${k}」`)
    }
  }
  for (const k of keys) {
    if (!(k in obj)) throw new Issue('missing_key', `${prefix}${k}`, `缺少必填的鍵「${k}」`)
  }
}

function requireStr(v: unknown, field: string): string {
  if (typeof v !== 'string') throw new Issue('invalid_type', field, `必須是字串，收到 ${pyType(v)}`)
  return v
}

function requireBool(v: unknown, field: string): boolean {
  if (typeof v !== 'boolean') throw new Issue('invalid_type', field, `必須是布林值，收到 ${pyType(v)}`)
  return v
}

/** 以 code point 計長度（後端 `len()` 的語意）。 */
function cpLen(s: string): number {
  return [...s].length
}

function checkChannel(raw: unknown) {
  const value = requireStr(raw, 'channel')
  if (!CHANNEL_RE.test(value)) {
    throw new Issue('invalid_value', 'channel', 'channel 只能用小寫英數與連字號、英數開頭、最長 32 字')
  }
}

function checkDns(raw: unknown) {
  const obj = requireObject(raw, 'dns')
  exactKeys(obj, DNS_KEYS, 'dns')
  const zone = obj.zone
  const managed = obj.clouddns_managed_zone
  if (zone !== null) {
    const z = requireStr(zone, 'dns.zone')
    if (!DOMAIN_RE.test(z)) throw new Issue('invalid_value', 'dns.zone', '必須是合法的小寫網域（例：example.com）')
  }
  if (managed !== null) {
    const m = requireStr(managed, 'dns.clouddns_managed_zone')
    if (!MANAGED_ZONE_RE.test(m)) {
      throw new Issue(
        'invalid_value',
        'dns.clouddns_managed_zone',
        'Cloud DNS zone 名只能用小寫英數與連字號、字母開頭、最長 63 字',
      )
    }
    if (zone === null) {
      throw new Issue(
        'invalid_value',
        'dns.clouddns_managed_zone',
        '指定 Cloud DNS zone 時必須同時指定 dns.zone',
      )
    }
  }
}

function checkPaths(raw: unknown) {
  const obj = requireObject(raw, 'paths')
  exactKeys(obj, PATH_KEYS, 'paths')
  const seen = new Map<string, string>()
  const values: [string, string][] = []
  for (const k of PATH_KEYS) {
    const field = `paths.${k}`
    const value = requireStr(obj[k], field)
    if (value.includes('..')) throw new Issue('invalid_value', field, '路徑不可包含「..」')
    if (value.split(SLUG_PLACEHOLDER).length - 1 !== 1) {
      throw new Issue('invalid_value', field, '路徑必須恰好包含一次 {slug}')
    }
    if (!PATH_RE.test(value)) {
      throw new Issue(
        'invalid_value',
        field,
        '路徑必須在 /opt/data 或 /opt/backup 底下、{slug} 在最後一段，只能用小寫英數、底線與連字號',
      )
    }
    values.push([k, value])
  }
  for (const [k, value] of values) {
    const prev = seen.get(value)
    if (prev) throw new Issue('invalid_value', `paths.${k}`, `與 paths.${prev} 相同；三個目錄必須兩兩不同`)
    seen.set(value, k)
  }
}

function checkMemLimits(raw: unknown) {
  const obj = requireObject(raw, 'mem_limits')
  const n = Object.keys(obj).length
  if (n < 1 || n > MEM_LIMITS_MAX_KEYS) {
    throw new Issue(
      'invalid_value',
      'mem_limits',
      `必須有 1–${MEM_LIMITS_MAX_KEYS} 個容器鍵，收到 ${n} 個`,
    )
  }
  for (const [k, v] of Object.entries(obj)) {
    const field = `mem_limits.${k}`
    if (!MEM_KEY_RE.test(k)) {
      throw new Issue('invalid_value', field, '容器鍵只能用小寫英數與底線、字母開頭、最長 24 字')
    }
    const text = requireStr(v, field)
    if (!MEM_VALUE_RE.test(text)) {
      throw new Issue('invalid_value', field, '記憶體上限的格式是「正整數＋k/m/g」（例：1g、384m）')
    }
  }
}

function checkResources(raw: unknown) {
  const obj = requireObject(raw, 'resources')
  exactKeys(obj, RESOURCE_KEYS, 'resources')
  for (const k of RESOURCE_KEYS) {
    const field = `resources.${k}`
    const v = obj[k]
    if (v === null) continue
    // 布林不算整數（後端 `type is int`），浮點也不收。
    if (typeof v !== 'number' || !Number.isInteger(v)) {
      throw new Issue('invalid_type', field, `必須是整數或 null，收到 ${pyType(v)}`)
    }
    const [low, high] = RESOURCE_RANGES[k]
    if (v < low || v > high) throw new Issue('invalid_value', field, `必須介於 ${low}–${high}`)
  }
}

function checkFeatures(raw: unknown): Record<string, boolean> {
  const obj = requireObject(raw, 'features')
  exactKeys(obj, FEATURE_KEYS, 'features')
  const out: Record<string, boolean> = {}
  for (const k of FEATURE_KEYS) out[k] = requireBool(obj[k], `features.${k}`)
  return out
}

function checkLoginMethods(raw: unknown, features: Record<string, boolean>) {
  if (!Array.isArray(raw)) throw new Issue('invalid_type', 'login_methods', `必須是陣列，收到 ${pyType(raw)}`)
  if (!raw.length) throw new Issue('invalid_value', 'login_methods', '至少要有一種登入方式')
  const out: string[] = []
  raw.forEach((item, i) => {
    const field = `login_methods[${i}]`
    const value = requireStr(item, field)
    if (!(LOGIN_METHODS as readonly string[]).includes(value)) {
      throw new Issue(
        'invalid_value',
        field,
        `不認得的登入方式「${value}」（可用：${LOGIN_METHODS.join('、')}）`,
      )
    }
    if (out.includes(value)) throw new Issue('invalid_value', field, `登入方式「${value}」重複`)
    out.push(value)
  })
  if (out.includes('google')) {
    for (const needed of ['sso_google_managed', 'tunnel']) {
      if (features[needed] !== true) {
        throw new Issue('invalid_value', 'login_methods', `允許 google 登入時 features.${needed} 必須為 true`)
      }
    }
  }
}

function checkSetupPath(raw: unknown) {
  const value = requireStr(raw, 'setup_path')
  if (cpLen(value) > SETUP_PATH_MAX_LEN) {
    throw new Issue('invalid_value', 'setup_path', `最長 ${SETUP_PATH_MAX_LEN} 字`)
  }
  if (!value.startsWith('/')) throw new Issue('invalid_value', 'setup_path', '必須以「/」開頭')
  if (!SETUP_PATH_RE.test(value)) {
    throw new Issue('invalid_value', 'setup_path', '含不允許的字元（空白、#、反斜線等）')
  }
  if (value.split(SETUP_TOKEN_PLACEHOLDER).length - 1 > 1) {
    throw new Issue('invalid_value', 'setup_path', '{setup_token} 最多只能出現一次')
  }
  const rest = value.split(SETUP_TOKEN_PLACEHOLDER).join('')
  if (rest.includes('{') || rest.includes('}')) {
    throw new Issue('invalid_value', 'setup_path', '只認得 {setup_token} 這一個佔位符')
  }
}

function checkSiteSecretItem(raw: unknown, index: number): { key: string; env: string } {
  const field = `site_secrets[${index}]`
  const obj = requireObject(raw, field)
  for (const k of Object.keys(obj)) {
    if (!(SITE_SECRET_REQUIRED as readonly string[]).includes(k) && !(SITE_SECRET_OPTIONAL as readonly string[]).includes(k)) {
      throw new Issue('unknown_key', `${field}.${k}`, `不認得的鍵「${k}」`)
    }
  }
  for (const k of SITE_SECRET_REQUIRED) {
    if (!(k in obj)) throw new Issue('missing_key', `${field}.${k}`, `缺少必填的鍵「${k}」`)
  }
  const key = requireStr(obj.key, `${field}.key`)
  if (!SITE_SECRET_KEY_RE.test(key)) {
    throw new Issue('invalid_value', `${field}.key`, '欄位鍵只能用小寫英數與底線、字母開頭、最長 32 字')
  }
  const env = requireStr(obj.env, `${field}.env`)
  if (!SITE_SECRET_ENV_RE.test(env)) {
    throw new Issue('invalid_value', `${field}.env`, '環境變數名只能用大寫英數與底線、字母開頭、2–64 字')
  }
  if (env.startsWith(SITE_SECRET_ENV_DENY_PREFIX)) {
    throw new Issue(
      'invalid_value',
      `${field}.env`,
      `環境變數名不可以 ${SITE_SECRET_ENV_DENY_PREFIX} 開頭（那是授權中心自己的設定）`,
    )
  }
  const label = requireStr(obj.label, `${field}.label`)
  if (!label.trim() || cpLen(label) > SITE_SECRET_LABEL_MAX) {
    throw new Issue('invalid_value', `${field}.label`, `名稱必須是 1–${SITE_SECRET_LABEL_MAX} 字`)
  }
  const hint = obj.hint
  if (hint !== undefined && hint !== null) {
    const h = requireStr(hint, `${field}.hint`)
    if (cpLen(h) > SITE_SECRET_HINT_MAX) {
      throw new Issue('invalid_value', `${field}.hint`, `說明最長 ${SITE_SECRET_HINT_MAX} 字`)
    }
  }
  return { key, env }
}

function checkSiteSecrets(raw: unknown) {
  if (!Array.isArray(raw)) throw new Issue('invalid_type', 'site_secrets', `必須是陣列，收到 ${pyType(raw)}`)
  if (raw.length > SITE_SECRETS_MAX) {
    throw new Issue('invalid_value', 'site_secrets', `最多 ${SITE_SECRETS_MAX} 個機密欄位，收到 ${raw.length} 個`)
  }
  const keys = new Set<string>()
  const envs = new Set<string>()
  raw.forEach((item, i) => {
    const { key, env } = checkSiteSecretItem(item, i)
    if (keys.has(key)) throw new Issue('invalid_value', `site_secrets[${i}].key`, `欄位鍵「${key}」重複`)
    if (envs.has(env)) throw new Issue('invalid_value', `site_secrets[${i}].env`, `環境變數名「${env}」重複`)
    keys.add(key)
    envs.add(env)
  })
}

/**
 * 嚴格預檢一份 profile；通過回 `null`，否則回**第一個**錯誤（與後端同一個）。
 * 檢查順序照後端：根 → 鍵集合 → version → features → channel → dns → paths →
 * mem_limits → resources → login_methods → setup_path →（有的話）site_secrets。
 */
export function validateProfile(raw: unknown): ProfileIssue | null {
  try {
    const obj = requireObject(raw, '')
    exactKeys(obj, TOP_KEYS, '', OPTIONAL_TOP_KEYS)
    const version = obj.version
    if (typeof version !== 'number' || !Number.isInteger(version)) {
      throw new Issue('invalid_type', 'version', `必須是整數，收到 ${pyType(version)}`)
    }
    if (version !== PROFILE_VERSION) {
      throw new Issue('invalid_value', 'version', `目前只支援 version ${PROFILE_VERSION}`)
    }
    const features = checkFeatures(obj.features)
    checkChannel(obj.channel)
    checkDns(obj.dns)
    checkPaths(obj.paths)
    checkMemLimits(obj.mem_limits)
    checkResources(obj.resources)
    checkLoginMethods(obj.login_methods, features)
    checkSetupPath(obj.setup_path)
    if ('site_secrets' in obj) checkSiteSecrets(obj.site_secrets)
    return null
  } catch (e) {
    if (e instanceof Issue) return { code: e.code, field: e.field, message: e.message }
    throw e
  }
}

/**
 * 表單層級的預檢：先抓「表單才會發生」的錯（記憶體上限兩列同一個鍵——轉成 JSON 時
 * 後一列會靜默蓋掉前一列），再交給 `validateProfile` 做與後端相同的檢查。
 */
export function validateForm(f: ProfileForm): ProfileIssue | null {
  const seen = new Set<string>()
  for (const row of f.mem) {
    const key = row.key.trim()
    if (!key && !row.value.trim()) continue
    if (seen.has(key)) {
      return { code: 'invalid_value', field: `mem_limits.${key}`, message: `容器鍵「${key}」重複` }
    }
    seen.add(key)
  }
  return validateProfile(profileFromForm(f))
}

/** 後端 422 的 `detail` 看起來像不像 profile 錯誤（`{code, field, message}`）。 */
export function asProfileIssue(detail: unknown): ProfileIssue | null {
  if (!isObj(detail)) return null
  const { code, field, message } = detail
  if (typeof code !== 'string' || typeof field !== 'string' || typeof message !== 'string') return null
  return { code, field, message }
}

// ---------------------------------------------------------------------------
// 讀取端的小工具
// ---------------------------------------------------------------------------

/** 從產品的 `options` 取原始 profile（可能缺席、可能是壞資料）。 */
export function rawProfileOf(options: Record<string, unknown> | null | undefined): unknown {
  return options ? options[PROFILE_KEY] : undefined
}

/** 產品目前的代管設定狀態：`none`＝沒有、`invalid`＝有但驗不過、`ok`＝有效。 */
export type ProfileState = 'none' | 'invalid' | 'ok'
export function profileState(options: Record<string, unknown> | null | undefined): ProfileState {
  const raw = rawProfileOf(options)
  if (raw === undefined || raw === null) return 'none'
  return validateProfile(raw) ? 'invalid' : 'ok'
}

/** 有效 profile 的功能旗標；缺席或無效＝null（呼叫端一律當成「全部不支援」）。 */
export function featuresOf(options: Record<string, unknown> | null | undefined): Record<FeatureKey, boolean> | null {
  const raw = rawProfileOf(options)
  if (raw === undefined || raw === null || validateProfile(raw)) return null
  return (raw as HostingProfile).features
}

/**
 * 功能旗標的讀取（`hosting_features` 投影、preflight 的 `profile.features` 共用）。
 * `null`／缺鍵一律 false——與後端「profile 缺席＝全部不支援」同一個 fail-closed 方向。
 */
export function hasFeature(
  features: Partial<Record<string, boolean>> | null | undefined,
  key: FeatureKey,
): boolean {
  return features?.[key] === true
}

/** setup 網址樣板需要 LS 產生一次性 token 嗎。 */
export function needsSetupToken(setupPath: string | null | undefined): boolean {
  return !!setupPath && setupPath.includes(SETUP_TOKEN_PLACEHOLDER)
}

/**
 * 表單上某一格有沒有被這個錯誤點名。`field` 是後端路徑；表單的「格」用同一套路徑，
 * 只有兩個例外要放寬：記憶體上限（`mem_limits.<鍵>` → 那一列）與登入方式
 * （`login_methods[i]` → 整組）。
 */
export function issueHits(issue: ProfileIssue | null, cell: string): boolean {
  if (!issue) return false
  if (issue.field === cell) return true
  if (cell === 'login_methods') return issue.field.startsWith('login_methods')
  if (cell === 'mem_limits') return issue.field === 'mem_limits'
  if (cell.startsWith('mem_limits.')) return issue.field === cell
  return false
}
